import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Plus, Eye, ToggleLeft, ToggleRight, X, AlertCircle, Trash2,
  Calendar, Users, Package, ShoppingCart, Database, Clock, Key, Gift,
  CheckCircle, XCircle, AlertTriangle, RefreshCw, Phone, Mail, MapPin,
  ArrowLeft, Building2, CreditCard, HardDrive, Image, Folder, Receipt,
  Edit, Shield, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Modal Components
const DeactivateModal = ({ isOpen, onClose, tenant, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      alert('Please provide a reason for deactivation');
      return;
    }
    setLoading(true);
    await onConfirm(reason);
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <AlertCircle className="text-red-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Deactivate Tenant</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>{tenant?.business_name}</strong> will be deactivated and won't be able to use the system.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason for Deactivation <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Enter the reason (e.g., Payment overdue, Policy violation, etc.)"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deactivating...' : 'Deactivate'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const GraceExtensionModal = ({ isOpen, onClose, tenant, onConfirm }) => {
  const [days, setDays] = useState(7);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!notes.trim()) {
      alert('Please provide a reason for the extension');
      return;
    }
    setLoading(true);
    await onConfirm(days, notes);
    setLoading(false);
    setNotes('');
    setDays(7);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Gift className="text-emerald-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Extend Grace Period</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">
              Extending subscription for <strong>{tenant?.business_name}</strong> without payment.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Extension Days</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value={7}>7 Days</option>
              <option value={15}>15 Days</option>
              <option value={30}>30 Days</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason / Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Enter reason for grace extension..."
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? 'Extending...' : 'Extend'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const PinModal = ({ isOpen, onClose, tenant, onGenerate, onRemove }) => {
  const [customPin, setCustomPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (useCustom = false) => {
    setLoading(true);
    await onGenerate(useCustom ? customPin : null);
    setLoading(false);
    setCustomPin('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-50 rounded-lg">
              <Key className="text-violet-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Admin PIN Management</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {tenant?.admin_pin && (
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
              <p className="text-sm text-violet-600 mb-1">Current PIN</p>
              <p className="text-2xl font-mono font-bold text-violet-800 tracking-widest">{tenant.admin_pin}</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Custom PIN (6 digits)</label>
            <input
              type="text"
              value={customPin}
              onChange={(e) => setCustomPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit PIN or leave empty for random"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono tracking-widest"
              maxLength={6}
            />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <button
              onClick={() => handleGenerate(customPin.length === 6)}
              disabled={loading}
              className="w-full bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {loading ? 'Generating...' : customPin.length === 6 ? 'Set Custom PIN' : 'Generate Random PIN'}
            </button>
            {tenant?.admin_pin && (
              <button
                onClick={onRemove}
                className="w-full bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 border border-red-200"
              >
                Remove PIN
              </button>
            )}
            <button onClick={onClose} className="w-full px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Edit Tenant Details Modal
const EditTenantModal = ({ isOpen, onClose, tenant, onSave }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    contact_person: '',
    mobile_number: '',
    email: '',
    address: '',
    postal_code: '',
    city: '',
    district: '',
    state: '',
    gst_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tenant) {
      setFormData({
        business_name: tenant.business_name || '',
        contact_person: tenant.contact_person || '',
        mobile_number: tenant.mobile_number || '',
        email: tenant.email || '',
        address: tenant.address || '',
        postal_code: tenant.postal_code || '',
        city: tenant.city || '',
        district: tenant.district || '',
        state: tenant.state || '',
        gst_number: tenant.gst_number || ''
      });
    }
  }, [tenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.business_name || !formData.contact_person || !formData.mobile_number) {
      setError('Business name, contact person and mobile number are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Edit className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Edit Tenant Details</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Business Name *</label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person *</label>
              <input
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number *</label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                maxLength={10}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
              <input
                type="text"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                placeholder="22AAAAA0000A1Z5"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Postal Code</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                maxLength={6}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">District</label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Manual Verify Modal
const ManualVerifyModal = ({ isOpen, onClose, tenant, onVerify }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!notes.trim()) {
      alert('Please provide notes explaining why manual verification is needed');
      return;
    }
    setLoading(true);
    await onVerify(notes);
    setLoading(false);
    setNotes('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Shield className="text-emerald-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Manual Verification</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>{tenant?.business_name}</strong> hasn't completed OTP verification. 
              Manual verification will allow them to login without OTP.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Verification Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Enter reason for manual verification (e.g., OTP delivery failed, verified via call, etc.)"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <FileText size={16} className="inline mr-2" />
            After verification, you can generate a PIN for the user to login.
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !notes.trim()}
              className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify & Approve'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Tenant Detail View Component
const TenantDetailView = ({ tenantId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [graceModal, setGraceModal] = useState(false);
  const [pinModal, setPinModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [storageDetails, setStorageDetails] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(false);

  useEffect(() => {
    fetchTenantDetails();
  }, [tenantId]);

  const fetchTenantDetails = async () => {
    try {
      const response = await axios.get(`${API}/admin/tenants/${tenantId}/detailed`);
      setData(response.data);
      // Auto-fetch storage details after tenant data loads
      fetchStorageDetails();
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageDetails = async () => {
    setLoadingStorage(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/tenants/${tenantId}/storage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageDetails(response.data);
    } catch (error) {
      console.error('Error fetching storage details:', error);
    } finally {
      setLoadingStorage(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/status`, {
        is_active: !data.tenant.is_active,
        deactivation_reason: data.tenant.is_active ? 'Deactivated by admin' : null
      });
      fetchTenantDetails();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleGraceExtension = async (days, notes) => {
    try {
      await axios.post(`${API}/admin/tenants/${tenantId}/extend-grace`, { days, notes });
      setGraceModal(false);
      fetchTenantDetails();
    } catch (error) {
      console.error('Error extending grace:', error);
    }
  };

  const handleUpdateDetails = async (formData) => {
    await axios.put(`${API}/admin/tenants/${tenantId}/details`, formData);
    fetchTenantDetails();
  };

  const handleManualVerify = async (notes) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/tenants/${tenantId}/manual-verify`, { notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTenantDetails();
    } catch (error) {
      console.error('Error verifying tenant:', error);
      alert(error.response?.data?.detail || 'Failed to verify tenant');
    }
  };

  const handleGeneratePin = async (customPin) => {
    try {
      const response = await axios.post(`${API}/admin/tenants/${tenantId}/generate-pin`, { 
        pin: customPin 
      });
      alert(`PIN Generated: ${response.data.pin}`);
      fetchTenantDetails();
    } catch (error) {
      console.error('Error generating PIN:', error);
      alert(error.response?.data?.detail || 'Failed to generate PIN');
    }
  };

  const handleRemovePin = async () => {
    try {
      await axios.delete(`${API}/admin/tenants/${tenantId}/admin-pin`);
      fetchTenantDetails();
      setPinModal(false);
    } catch (error) {
      console.error('Error removing PIN:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'not_selected': { color: 'bg-slate-100 text-slate-600', label: 'No Plan Selected' },
      'pending_approval': { color: 'bg-amber-100 text-amber-700', label: 'Pending Approval' },
      'pending_activation': { color: 'bg-blue-100 text-blue-700', label: 'Pending Activation' },
      'active': { color: 'bg-emerald-100 text-emerald-700', label: 'Active' },
      'expired': { color: 'bg-red-100 text-red-700', label: 'Expired' }
    };
    return badges[status] || badges['not_selected'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12 text-slate-500">Tenant not found</div>;
  }

  const { tenant, subscription, usage, pending_payments } = data;
  const statusBadge = getStatusBadge(subscription.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {tenant.business_name}
            </h1>
            <p className="text-slate-500">Store ID: {tenant.store_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${tenant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {tenant.is_active ? 'Active' : 'Deactivated'}
          </span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.color}`}>
            {statusBadge.label}
          </span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <ShoppingCart size={16} />
            <span>Total Bills</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{usage.total_bills}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <CreditCard size={16} />
            <span>Total Sales</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">₹{usage.total_sales?.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Package size={16} />
            <span>Products</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{usage.products_count}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Users size={16} />
            <span>Cashiers</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{usage.cashiers_count}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Database size={16} />
            <span>Storage</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{usage.storage_used_mb?.toFixed(1)} MB</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Clock size={16} />
            <span>Last Login</span>
          </div>
          <p className="text-sm font-medium text-slate-800">{tenant.last_login_at ? formatDate(tenant.last_login_at) : 'Never'}</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenant Information */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="text-violet-600" size={20} />
            <h3 className="font-semibold text-slate-800">Tenant Information</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Contact Person</span>
              <span className="font-medium">{tenant.contact_person}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500 flex items-center gap-1"><Phone size={14} /> Mobile</span>
              <span className="font-medium">{tenant.mobile_number}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500 flex items-center gap-1"><Mail size={14} /> Email</span>
              <span className="font-medium">{tenant.email || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500 flex items-center gap-1"><MapPin size={14} /> Address</span>
              <span className="font-medium text-right max-w-[200px]">{tenant.address}, {tenant.city}, {tenant.state} - {tenant.postal_code}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">GST Number</span>
              <span className="font-medium font-mono">{tenant.gst_number || '-'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Registration Date</span>
              <span className="font-medium">{formatDate(tenant.registration_date)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Approval Date</span>
              <span className="font-medium">{tenant.approval_date ? formatDate(tenant.approval_date) : 'Not approved yet'}</span>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="text-emerald-600" size={20} />
              <h3 className="font-semibold text-slate-800">Subscription Details</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.color}`}>
              {statusBadge.label}
            </span>
          </div>
          
          {subscription.plan_name ? (
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Plan Name</span>
                <span className="font-semibold text-violet-600">{subscription.plan_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Price</span>
                <span className="font-medium">₹{subscription.plan_price?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Duration</span>
                <span className="font-medium">{subscription.duration_months} months</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Bills Limit</span>
                <span className="font-medium">{subscription.bills_limit || 'Unlimited'} / month</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Storage Limit</span>
                <span className="font-medium">{subscription.storage_limit_mb} MB</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Valid Until</span>
                <span className={`font-medium ${subscription.status === 'expired' ? 'text-red-600' : 'text-emerald-600'}`}>
                  {subscription.valid_until ? formatDate(subscription.valid_until) : '-'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Next Renewal</span>
                <span className="font-medium">{subscription.valid_until ? formatDate(subscription.valid_until) : '-'}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <CreditCard size={48} className="mx-auto mb-3 opacity-50" />
              <p>No plan selected yet</p>
            </div>
          )}

          {/* Pending Payments */}
          {pending_payments?.length > 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="font-medium text-amber-800 mb-2">Pending Payment Approvals</p>
              {pending_payments.map((payment, idx) => (
                <div key={idx} className="text-sm text-amber-700">
                  ₹{payment.amount} - {payment.payment_mode}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Grace Extensions History */}
      {tenant.grace_extensions?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="text-emerald-600" size={20} />
            <h3 className="font-semibold text-slate-800">Grace Period Extensions History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Date</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Days</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Previous Expiry</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">New Expiry</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Notes</th>
                </tr>
              </thead>
              <tbody>
                {tenant.grace_extensions.map((ext, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="px-4 py-3 text-sm">{formatDate(ext.extended_on)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-emerald-600">+{ext.days} days</td>
                    <td className="px-4 py-3 text-sm">{ext.previous_expiry ? formatDate(ext.previous_expiry) : '-'}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(ext.new_expiry)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{ext.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Storage Breakdown Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <HardDrive className="text-blue-500" size={20} />
            <h3 className="font-semibold text-slate-800">Storage Breakdown</h3>
          </div>
          <button
            onClick={fetchStorageDetails}
            disabled={loadingStorage}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loadingStorage ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {loadingStorage && !storageDetails ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
        ) : storageDetails ? (
          <div className="space-y-6">
            {/* Summary Bar */}
            <div className={`p-4 rounded-lg ${
              storageDetails.storage_summary?.critical ? 'bg-red-50 border border-red-200' :
              storageDetails.storage_summary?.warning ? 'bg-amber-50 border border-amber-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Total Storage</span>
                <span className="text-lg font-bold">
                  {storageDetails.storage_summary?.used_mb < 1 
                    ? `${(storageDetails.storage_summary?.used_mb * 1024).toFixed(0)} KB`
                    : `${storageDetails.storage_summary?.used_mb?.toFixed(2)} MB`}
                  {' / '}
                  {storageDetails.storage_summary?.limit_mb >= 1024 
                    ? `${(storageDetails.storage_summary?.limit_mb / 1024).toFixed(1)} GB`
                    : `${storageDetails.storage_summary?.limit_mb} MB`}
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    storageDetails.storage_summary?.critical ? 'bg-red-500' :
                    storageDetails.storage_summary?.warning ? 'bg-amber-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(storageDetails.storage_summary?.percentage || 0, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 text-right">
                {storageDetails.storage_summary?.percentage?.toFixed(1)}% used
              </p>
            </div>

            {/* Images & Attachments */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Image className="text-violet-500" size={18} />
                <h4 className="font-medium text-slate-700">Images & Attachments</h4>
                <span className="ml-auto text-sm font-semibold text-violet-600">
                  {((storageDetails.images_and_attachments?.total_mb || 0) * 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                  <p className="text-xs text-violet-600 mb-1">Product Images</p>
                  <p className="text-lg font-bold text-violet-700">
                    {storageDetails.images_and_attachments?.breakdown?.product_images?.count || 0}
                  </p>
                  <p className="text-xs text-slate-500">
                    {((storageDetails.images_and_attachments?.breakdown?.product_images?.size_mb || 0) * 1024).toFixed(1)} KB
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 mb-1">Business Logo</p>
                  <p className="text-lg font-bold text-blue-700">
                    {storageDetails.images_and_attachments?.breakdown?.business_logo?.count || 0}
                  </p>
                  <p className="text-xs text-slate-500">
                    {((storageDetails.images_and_attachments?.breakdown?.business_logo?.size_mb || 0) * 1024).toFixed(1)} KB
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-1">Payment Attachments</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {storageDetails.images_and_attachments?.breakdown?.payment_attachments?.count || 0}
                  </p>
                  <p className="text-xs text-slate-500">
                    {((storageDetails.images_and_attachments?.breakdown?.payment_attachments?.size_mb || 0) * 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            </div>

            {/* Master Data */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Folder className="text-amber-500" size={18} />
                <h4 className="font-medium text-slate-700">Master Data</h4>
                <span className="ml-auto text-sm font-semibold text-amber-600">
                  {((storageDetails.master_data?.total || 0) * 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {storageDetails.master_data?.counts && Object.entries(storageDetails.master_data.counts).map(([key, count]) => (
                  <div key={key} className="p-2 bg-amber-50 rounded-lg border border-amber-100 text-center">
                    <p className="text-xs text-amber-600 capitalize">{key}</p>
                    <p className="text-lg font-bold text-amber-700">{count}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Transaction Data */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="text-rose-500" size={18} />
                <h4 className="font-medium text-slate-700">Transaction Data</h4>
                <span className="ml-auto text-sm font-semibold text-rose-600">
                  {((storageDetails.transaction_data?.total || 0) * 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {storageDetails.transaction_data?.counts && Object.entries(storageDetails.transaction_data.counts).map(([key, count]) => (
                  <div key={key} className="p-2 bg-rose-50 rounded-lg border border-rose-100 text-center">
                    <p className="text-xs text-rose-600 capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-bold text-rose-700">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <HardDrive size={48} className="mx-auto mb-3 opacity-50" />
            <p>Click "Refresh" to load storage breakdown</p>
          </div>
        )}
      </div>

      {/* Admin Controls */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4">Admin Controls</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {/* Edit Details Button */}
          <button
            onClick={() => setEditModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
          >
            <Edit size={18} />
            Edit Details
          </button>
          
          {/* Manual Verify Button - Only show if not verified */}
          {!tenant.is_verified && (
            <button
              onClick={() => setVerifyModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200"
            >
              <Shield size={18} />
              Manual Verify
            </button>
          )}
          
          <button
            onClick={handleToggleStatus}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              tenant.is_active 
                ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200' 
                : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            {tenant.is_active ? <XCircle size={18} /> : <CheckCircle size={18} />}
            {tenant.is_active ? 'Deactivate' : 'Activate'}
          </button>
          
          <button
            onClick={() => setGraceModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200"
          >
            <Gift size={18} />
            Extend Grace
          </button>
          
          <button
            onClick={() => setPinModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-violet-50 text-violet-600 hover:bg-violet-100 border border-violet-200"
          >
            <Key size={18} />
            {tenant.admin_pin ? 'Manage PIN' : 'Generate PIN'}
          </button>
          
          <button
            onClick={fetchTenantDetails}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
        
        {/* Verification Status */}
        {tenant.is_verified ? (
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="text-emerald-600" size={20} />
            <div>
              <p className="text-sm font-medium text-emerald-700">Mobile Verified</p>
              {tenant.verification_type === 'admin_manual' && (
                <p className="text-xs text-emerald-600">Manually verified by admin: {tenant.verification_notes}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="text-amber-600" size={20} />
            <div>
              <p className="text-sm font-medium text-amber-700">Mobile Not Verified</p>
              <p className="text-xs text-amber-600">User cannot login until verified via OTP or manual approval</p>
            </div>
          </div>
        )}
        
        {tenant.admin_pin && (
          <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-lg">
            <p className="text-sm text-violet-600 mb-1">Admin Override PIN (for OTP bypass)</p>
            <p className="text-2xl font-mono font-bold text-violet-800 tracking-widest">{tenant.admin_pin}</p>
          </div>
        )}
        
        {tenant.deactivation_reason && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 mb-1">Deactivation Reason</p>
            <p className="text-red-800">{tenant.deactivation_reason}</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <GraceExtensionModal
        isOpen={graceModal}
        onClose={() => setGraceModal(false)}
        tenant={tenant}
        onConfirm={handleGraceExtension}
      />
      <PinModal
        isOpen={pinModal}
        onClose={() => setPinModal(false)}
        tenant={tenant}
        onGenerate={handleGeneratePin}
        onRemove={handleRemovePin}
      />
      <EditTenantModal
        isOpen={editModal}
        onClose={() => setEditModal(false)}
        tenant={tenant}
        onSave={handleUpdateDetails}
      />
      <ManualVerifyModal
        isOpen={verifyModal}
        onClose={() => setVerifyModal(false)}
        tenant={tenant}
        onVerify={handleManualVerify}
      />
    </div>
  );
};

// Upgrade Plan Modal
const UpgradePlanModal = ({ tenant, plans, onClose, onSuccess }) => {
  const [planId, setPlanId] = useState(tenant.tenant.subscription_plan_id || '');
  const [extendDays, setExtendDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/admin/upload-attachment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachmentUrl(resp.data.url);
    } catch (err) { alert('Upload failed'); } finally { setUploading(false); }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tenants/${tenant.tenant.id}/upgrade-plan`, null, {
        params: { plan_id: planId, extend_days: extendDays, notes, attachment_url: attachmentUrl }
      });
      onSuccess();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Upgrade / Extend Plan</h3>
          <p className="text-sm text-slate-500">{tenant.tenant.business_name} ({tenant.tenant.store_id})</p>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Change Plan</label>
            <select value={planId} onChange={e => setPlanId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="upgrade-plan-select">
              <option value="">Keep Current Plan</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name} - Rs.{p.price}/mo</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Extend Validity (Days)</label>
            <div className="flex gap-2">
              {[30, 90, 180, 365].map(d => (
                <button key={d} onClick={() => setExtendDays(d)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${extendDays === d ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{d}d</button>
              ))}
              <input type="number" value={extendDays} onChange={e => setExtendDays(parseInt(e.target.value) || 0)} min={1} max={365} className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Reason for upgrade, payment reference..." data-testid="upgrade-notes" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Attachment (optional)</label>
            {attachmentUrl ? (
              <div className="flex items-center gap-2">
                <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 underline">View</a>
                <button onClick={() => setAttachmentUrl('')} className="text-xs text-red-600">Remove</button>
              </div>
            ) : (
              <input type="file" accept="image/*,.pdf" onChange={handleAttachmentUpload} disabled={uploading}
                className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-slate-100 file:text-slate-700"
                data-testid="upgrade-attachment" />
            )}
            {uploading && <p className="text-xs text-violet-600 mt-1">Uploading...</p>}
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium">Cancel</button>
            <button onClick={handleSubmit} disabled={saving || (!planId && !extendDays)} className="flex-1 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50" data-testid="upgrade-submit-btn">
              {saving ? 'Saving...' : 'Apply Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Tenants Component
const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState({ isOpen: false, tenant: null });
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTenants();
    fetchPlans();
  }, [statusFilter]);

  const fetchTenants = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get(`${API}/admin/tenants`, { params });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const resp = await axios.get(`${API}/admin/plans`);
      setPlans(resp.data || []);
    } catch (e) {}
  };

  const handleDeactivateConfirm = async (reason) => {
    try {
      await axios.put(`${API}/admin/tenants/${deactivateModal.tenant.id}/status`, {
        is_active: false,
        deactivation_reason: reason,
      });
      setDeactivateModal({ isOpen: false, tenant: null });
      fetchTenants();
    } catch (error) {
      console.error('Error deactivating tenant:', error);
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/status`, {
        is_active: true,
        deactivation_reason: null,
      });
      fetchTenants();
    } catch (error) {
      console.error('Error activating tenant:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getPlanStatus = (t) => {
    if (!t.tenant.subscription_plan_id) return { label: 'No Plan', color: 'bg-slate-100 text-slate-600' };
    if (!t.tenant.subscription_valid_until) return { label: 'Pending', color: 'bg-amber-100 text-amber-700' };
    const isExpired = new Date(t.tenant.subscription_valid_until) < new Date();
    if (isExpired) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
    return { label: t.plan?.name || 'Active', color: 'bg-emerald-100 text-emerald-700' };
  };

  const isExpiringSoon = (t) => {
    if (!t.tenant.subscription_valid_until) return false;
    const expiry = new Date(t.tenant.subscription_valid_until);
    const now = new Date();
    const diffDays = (expiry - now) / (1000 * 60 * 60 * 24);
    return diffDays > 0 && diffDays <= 30;
  };

  const filteredTenants = tenants.filter((t) => {
    const searchLower = search.toLowerCase();
    return (
      t.tenant.business_name.toLowerCase().includes(searchLower) ||
      t.tenant.store_id.toLowerCase().includes(searchLower) ||
      t.tenant.mobile_number.includes(searchLower)
    );
  });

  // Show detail view if tenant selected
  if (selectedTenant) {
    return <TenantDetailView tenantId={selectedTenant} onBack={() => setSelectedTenant(null)} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="tenants-list" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Tenants</h1>
          <p className="text-slate-600">{filteredTenants.length} businesses ({statusFilter})</p>
        </div>
        <button
          onClick={() => navigate('/admin/tenants/new')}
          className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-lg"
        >
          <Plus size={20} />
          Add Tenant
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by business name, store ID, or mobile..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'active', label: 'Active', color: 'emerald' },
            { key: 'inactive', label: 'Suspended', color: 'red' },
            { key: 'expired', label: 'Expired', color: 'amber' },
            { key: 'all', label: 'All', color: 'slate' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                statusFilter === tab.key ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tenants Table */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Store</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Expiry</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Last Activity</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map((t) => {
                const planStatus = getPlanStatus(t);
                const expiringSoon = isExpiringSoon(t);
                return (
                  <tr key={t.tenant.id} className={`border-b border-slate-100 hover:bg-slate-50 ${expiringSoon ? 'bg-amber-50' : ''}`}>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-slate-800">{t.tenant.business_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{t.tenant.store_id}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm">{t.tenant.contact_person}</p>
                      <p className="text-xs text-slate-500">{t.tenant.mobile_number}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${planStatus.color}`}>
                        {planStatus.label}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-sm ${expiringSoon ? 'text-amber-700 font-semibold' : ''}`}>
                        {t.tenant.subscription_valid_until ? formatDate(t.tenant.subscription_valid_until) : '-'}
                      </span>
                      {expiringSoon && <p className="text-xs text-amber-600 mt-0.5">Expiring soon</p>}
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-500">
                      {formatDateTime(t.tenant.last_activity)}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        t.tenant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {t.tenant.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedTenant(t.tenant.id)}
                          className="p-2 hover:bg-violet-100 rounded-lg text-violet-600"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => setUpgradeModal(t)}
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
                          title="Upgrade/Extend Plan"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
                        </button>
                        <button
                          onClick={() => t.tenant.is_active 
                            ? setDeactivateModal({ isOpen: true, tenant: t.tenant })
                            : handleActivate(t.tenant.id)
                          }
                          className={`p-2 rounded-lg ${
                            t.tenant.is_active ? 'hover:bg-red-100 text-red-600' : 'hover:bg-emerald-100 text-emerald-600'
                          }`}
                          title={t.tenant.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {t.tenant.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deactivate Modal */}
      <DeactivateModal
        isOpen={deactivateModal.isOpen}
        onClose={() => setDeactivateModal({ isOpen: false, tenant: null })}
        tenant={deactivateModal.tenant}
        onConfirm={handleDeactivateConfirm}
      />

      {/* Upgrade/Extend Plan Modal */}
      {upgradeModal && (
        <UpgradePlanModal
          tenant={upgradeModal}
          plans={plans}
          onClose={() => setUpgradeModal(null)}
          onSuccess={() => { setUpgradeModal(null); fetchTenants(); }}
        />
      )}
    </div>
  );
};

export default Tenants;
